/**
 * Edge Repository
 * Data access for edges (connections between notes)
 * 
 * @module repositories/edgeRepository
 */

import { supabase } from '../services/supabase';
import { ApiError } from '../lib/errors';
import { logger } from '../lib/logger';

// =============================================================================
// Types
// =============================================================================

export interface EdgeRow {
    id: string;
    page_id: string;
    from_id: string;
    to_id: string;
    label: string | null;
    style: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
}

export interface EdgeInsert {
    id: string;
    page_id: string;
    from_id: string;
    to_id: string;
    label?: string;
    style?: Record<string, unknown>;
}

// =============================================================================
// Repository
// =============================================================================

export const edgeRepository = {
    /**
     * Find all edges for a page
     */
    async findByPageId(pageId: string): Promise<EdgeRow[]> {
        logger.debug('[EdgeRepo] findByPageId', { pageId });

        const { data, error } = await supabase
            .from('edges')
            .select('*')
            .eq('page_id', pageId);

        if (error) {
            throw new ApiError('Failed to fetch edges', {
                statusCode: 500,
                context: { pageId, error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Batch upsert edges
     */
    async upsertMany(edges: EdgeInsert[]): Promise<EdgeRow[]> {
        if (edges.length === 0) return [];

        logger.debug('[EdgeRepo] upsertMany', { count: edges.length });

        const { data, error } = await supabase
            .from('edges')
            .upsert(
                edges.map(e => ({
                    ...e,
                    updated_at: new Date().toISOString(),
                })),
                { onConflict: 'id' }
            )
            .select();

        if (error) {
            throw new ApiError('Failed to upsert edges', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Delete an edge
     */
    async delete(id: string): Promise<void> {
        const { error } = await supabase
            .from('edges')
            .delete()
            .eq('id', id);

        if (error) {
            throw new ApiError('Failed to delete edge', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }
    },

    /**
     * Delete edges by IDs
     */
    async deleteMany(ids: string[]): Promise<void> {
        if (ids.length === 0) return;

        const { error } = await supabase
            .from('edges')
            .delete()
            .in('id', ids);

        if (error) {
            throw new ApiError('Failed to delete edges', {
                statusCode: 500,
                context: { ids, error: error.message },
            });
        }
    },
};
